import React, { useEffect, useState } from 'react';
import apiDefinitions from '../../../core/api/apiDefinitions';
import { showError } from '../../../core/alert/sweetAlertConfig';

interface LetterDTO {
  id: number;
  date: string;
  referenceNo: string;
  letterType: string;
  senderDetails: string;
  letterDescription: string;
  status: string;
  handovers: any[];
}

interface EditLetterModuleProps {
  show: boolean;
  onHide: () => void;
  letterId: number;
  onLetterUpdated: (payload: {
    id: number;
    date?: string;
    referenceNo?: string;
    letterType?: string;
    senderDetails?: string;
    letterDescription?: string;
  }) => void;
}

const EditLetterModule: React.FC<EditLetterModuleProps> = ({ show, onHide, letterId, onLetterUpdated }) => {
  const [formData, setFormData] = useState({
    id: letterId,
    date: '',
    referenceNo: '',
    letterType: 'REGISTER_POST',
    senderDetails: '',
    letterDescription: '',
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (show && letterId) {
      setIsLoading(true);
      apiDefinitions
        .getLetterById(letterId)
        .then((res) => {
          if (res.status === 200 && res.data.status === 'S1000') {
            const letter: LetterDTO = res.data.data;
            setFormData({
              id: letter.id,
              date: new Date(letter.date).toISOString().split('T')[0], // Format for input[type=date]
              referenceNo: letter.referenceNo,
              letterType: letter.letterType,
              senderDetails: letter.senderDetails,
              letterDescription: letter.letterDescription || '',
            });
          } else {
            showError(res.data?.statusDescription || 'Failed to fetch letter details');
          }
        })
        .catch((e) => {
          showError('Failed to fetch letter details. Please try again.');
        })
        .finally(() => setIsLoading(false));
    }
  }, [show, letterId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onLetterUpdated(formData);
  };

  return (
    <div className="modal custom-modal fade show" id="editLetterModal" role="dialog" style={{ display: show ? 'block' : 'none' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Edit Letter</h5>
            <button className="btn-close" onClick={onHide} aria-label="Close">
              <i className="ti ti-x" />
            </button>
          </div>
          <div className="modal-body">
            {isLoading ? (
              <div className="text-center">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="col-form-label">Date <span className="text-danger">*</span></label>
                  <input
                    type="date"
                    className="form-control"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="col-form-label">Reference No <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    name="referenceNo"
                    value={formData.referenceNo}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="col-form-label">Letter Type <span className="text-danger">*</span></label>
                  <select
                    className="form-control"
                    name="letterType"
                    value={formData.letterType}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="REGISTER_POST">Registered Post</option>
                    <option value="NORMAL_POST">Normal Post</option>
                    <option value="PARCEL">Parcel</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label className="col-form-label">Sender Details <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    name="senderDetails"
                    value={formData.senderDetails}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="col-form-label">Description</label>
                  <input
                    type="text"
                    className="form-control"
                    name="letterDescription"
                    value={formData.letterDescription}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="modal-btn">
                  <button type="button" className="btn btn-primary" onClick={onHide}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-success">
                    Save
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditLetterModule;